import { lazy, Suspense, useRef, useEffect, useState } from 'react';
import { Link } from "@/lib/router-compat";
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/hooks/useAnalytics';
import { CalendarHeart, ArrowRight, Sparkles } from 'lucide-react';
import { HeroDiningPicture } from '@/components/landing/HeroDiningPicture';

import { HeroTrustBar } from '@/components/landing/HeroTrustBar';
import { ExperienceNav } from '@/components/landing/ExperienceNav';
import { HomeNav } from '@/components/home/HomeNav';
import { ThreeSteps, WhatToExpect, MembershipBand, HomeFAQ } from '@/components/home/HomeSections';
import { FoundingInvite } from '@/components/founding/FoundingInvite';
import { DeferredSection } from '@/components/perf/DeferredSection';

// Below-the-fold sections: code-split + mounted via IntersectionObserver
const Footer = lazy(() => import('@/components/Footer'));
const CitiesSection = lazy(() => import('@/components/cities/CitiesSection'));
const SocialProof = lazy(() =>
  import('@/components/landing/SocialProof').then((m) => ({ default: m.SocialProof })),
);
const TrustSafetySection = lazy(() =>
  import('@/components/landing/TrustSafetySection').then((m) => ({ default: m.TrustSafetySection })),
);
const CookieConsent = lazy(() =>
  import('@/components/CookieConsent').then((m) => ({ default: m.CookieConsent })),
);
const PublicSupportChat = lazy(() =>
  import('@/components/support/PublicSupportChat').then((m) => ({ default: m.PublicSupportChat })),
);

/* ── Brand Hero ────────────────────────────────────────── */
const BrandHero = () => {
  const heroRef = useRef(null);
  // Parallax runs only on precise-pointer (desktop) devices — scroll-linked
  // transforms are a known jank source on iPad/iPhone Safari.
  const [parallax, setParallax] = useState(false);
  useEffect(() => {
    setParallax(window.matchMedia('(pointer: fine)').matches);
  }, []);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={heroRef} className="relative min-h-[100svh] flex flex-col justify-center pt-20 pb-16 px-4 overflow-hidden">
      {/* Parallax background — members in conversation over dinner */}
      <motion.div className="absolute inset-0 pointer-events-none" style={parallax ? { y: bgY } : {}}>
        <HeroDiningPicture
          alt="A diverse group of adults laughing in conversation over dinner at an elegant restaurant"
          className="absolute inset-0 w-full h-full object-cover object-[center_30%] md:object-center"
        />
      </motion.div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-[hsl(var(--background))]/75" />
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--background))]/85 via-[hsl(var(--background))]/40 to-[hsl(var(--background))]/90" />

      {/* Entrance animation is pure CSS (am-fade-up) so above-the-fold content
          paints even if the JS bundle fails; framer-motion only adds parallax. */}
      <motion.div
        className="relative max-w-2xl mx-auto text-center"
        style={parallax ? { y: contentY, opacity: contentOpacity } : {}}
      >
        <p
          className="am-fade-up text-xs md:text-sm uppercase tracking-[0.25em] text-primary/80 font-semibold mb-4"
          style={{ animationDelay: '0.25s' }}
        >
          Curated Dinners · Thoughtful Introductions
        </p>

        <h1
          className="am-fade-up text-[2rem] md:text-5xl lg:text-6xl font-display font-semibold text-foreground mb-5 md:mb-6 leading-[1.12] tracking-tight"
          style={{ animationDelay: '0.43s', textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
        >
          A Great Evening Starts with the{' '}
          <span className="text-gold-glow">Right Company</span>
        </h1>

        <p
          className="am-fade-up text-base md:text-lg text-muted-foreground max-w-lg mx-auto mb-10 md:mb-12 leading-relaxed"
          style={{ animationDelay: '0.61s' }}
        >
          Curated dinners and thoughtful introductions for people ready to connect in person.
        </p>

        <div className="am-fade-up flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" style={{ animationDelay: '0.79s' }}>
          <Link to="/quiz" className="w-full sm:w-auto">
            <Button
              variant="luxury"
              className="w-full sm:w-auto gap-2 rounded-full h-11 md:h-12 px-6 md:px-7 text-sm md:text-[15px] font-semibold"
            >
              <Sparkles className="h-4 w-4" />
              Find My Experience
            </Button>
          </Link>
          <a href="#how-it-works" className="w-full sm:w-auto">
            <Button
              variant="outline"
              className="w-full sm:w-auto gap-2 rounded-full h-11 md:h-12 px-6 md:px-7 text-sm md:text-[15px] font-semibold border-primary/30 bg-secondary/50 backdrop-blur-sm"
            >
              See How It Works
              <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
        </div>

        <div className="am-fade-up mt-14 md:mt-16" style={{ animationDelay: '0.97s' }}>
          <HeroTrustBar />
        </div>
      </motion.div>
    </section>
  );
};

/* ── Closing CTA ───────────────────────────────────────── */
const BrandClosingCTA = () => (
  <section className="py-24 md:py-32 px-4">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      variants={fadeInUp}
      className="max-w-2xl mx-auto text-center"
    >
      <CalendarHeart className="h-10 w-10 mx-auto mb-6 text-primary/60" />
      <h2 className="font-display text-2xl md:text-4xl font-semibold text-foreground mb-5 leading-tight">
        Your Table Is{' '}
        <span className="text-gold-glow">Waiting</span>
      </h2>
      <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
        A few thoughtful questions, one well-chosen evening, and company worth meeting.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/quiz">
          <Button variant="luxury" className="rounded-full h-11 md:h-12 px-6 text-sm md:text-[15px] gap-2 font-semibold">
            <Sparkles className="h-4 w-4" />
            Find My Experience
          </Button>
        </Link>
        <Link to="/events">
          <Button variant="outline" className="rounded-full h-11 md:h-12 px-6 text-sm md:text-[15px] gap-2 font-semibold border-primary/30 bg-secondary/50">
            Browse Experiences
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </motion.div>
  </section>
);

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' as const } },
};

/* ── Home Page ─────────────────────────────────────────── */
const Home = () => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('home');
  }, [trackPageView]);

  return (
    <div className="min-h-screen bg-gradient-soft overflow-hidden">
      <HomeNav />
      <BrandHero />
      <ThreeSteps />
      <ExperienceNav />
      <Suspense fallback={null}>
        <DeferredSection minHeight={400}>
          <CitiesSection maxCities={20} />
        </DeferredSection>
      </Suspense>
      <FoundingInvite />
      <MembershipBand />
      <WhatToExpect />
      <Suspense fallback={null}>
        <DeferredSection minHeight={400}>
          <SocialProof />
        </DeferredSection>
        <DeferredSection minHeight={300}>
          <TrustSafetySection />
        </DeferredSection>
      </Suspense>
      <HomeFAQ />
      <BrandClosingCTA />
      <Suspense fallback={null}>
        <Footer />
        <CookieConsent />
        <PublicSupportChat />
      </Suspense>
    </div>
  );
};

export default Home;
